import { useState } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Truck, Plus, BarChart3 } from "lucide-react";
import { useDeliverers } from "@/hooks/useDeliverers";
import { DelivererForm } from "@/components/deliverers/DelivererForm";
import { DelivererDetail } from "@/components/deliverers/DelivererDetail";
import { RatingStars } from "@/components/deliverers/RatingStars";

const STATUS_COLORS = {
  available: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  delivering: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  offline: "bg-gray-500/10 text-gray-400 border-gray-500/20",
};
const STATUS_LABELS = { available: "Livre", delivering: "Em entrega", offline: "Offline" };

export default function DeliverersPage() {
  const { deliverers, loading, fetchDeliverers, toggleActive } = useDeliverers();
  const [showNew, setShowNew] = useState(false);
  const [tab, setTab] = useState("motorizados");
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");

  const filtered = deliverers.filter(d =>
    d.name.toLowerCase().includes(search.toLowerCase())
  );

  if (selectedId) {
    return (
      <MainLayout>
        <DelivererDetail
          delivererId={selectedId}
          onBack={() => { setSelectedId(null); fetchDeliverers(); }}
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="deliverers-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Entregadores</h1>
            <p className="text-muted-foreground mt-1">Gestao da equipe de entrega</p>
          </div>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="new-deliverer-btn">
                <Plus className="w-4 h-4" /> Adicionar Entregador
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DelivererForm onClose={() => { setShowNew(false); fetchDeliverers(); }} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-secondary rounded-lg p-1" data-testid="deliverer-tabs">
          {[
            { id: "motorizados", label: "Motorizados", icon: Truck },
            { id: "ranking", label: "Ranking", icon: BarChart3 },
          ].map(t => (
            <button
              key={t.id}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                tab === t.id ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {loading ? <LoadingScreen /> : (
          <>
            {tab === "motorizados" && (
              <>
                <Input
                  placeholder="Buscar por nome..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="max-w-sm"
                  data-testid="search-deliverer"
                />
                <Card>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="deliverers-table">
                      <thead>
                        <tr className="border-b border-border/50 bg-secondary/30">
                          <th className="text-left p-3 font-medium">Nome</th>
                          <th className="text-left p-3 font-medium">Telefone</th>
                          <th className="text-left p-3 font-medium">Status</th>
                          <th className="text-center p-3 font-medium">Entregas</th>
                          <th className="text-center p-3 font-medium">Avaliacao</th>
                          <th className="text-center p-3 font-medium">Ativo</th>
                          <th className="text-left p-3 font-medium">Token</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map(d => (
                          <tr
                            key={d.id}
                            className="border-b border-border/30 hover:bg-secondary/20 transition-colors cursor-pointer"
                            onClick={() => setSelectedId(d.id)}
                            data-testid={`deliverer-row-${d.name.toLowerCase().replace(/\s/g, "-")}`}
                          >
                            <td className="p-3"><span className="font-medium text-primary hover:underline">{d.name}</span></td>
                            <td className="p-3 text-muted-foreground">{d.phone}</td>
                            <td className="p-3">
                              <Badge variant="outline" className={STATUS_COLORS[d.status]}>
                                {STATUS_LABELS[d.status] || d.status}
                              </Badge>
                            </td>
                            <td className="p-3 text-center font-medium">{d.total_deliveries || 0}</td>
                            <td className="p-3">
                              <div className="flex items-center justify-center gap-1">
                                {(d.total_ratings || 0) > 0 ? (
                                  <>
                                    <RatingStars rating={d.avg_rating || 0} />
                                    <span className="text-xs ml-1">{(d.avg_rating || 0).toFixed(1)}</span>
                                  </>
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </div>
                            </td>
                            <td className="p-3 text-center" onClick={e => e.stopPropagation()}>
                              <Switch
                                checked={d.active !== false}
                                onCheckedChange={() => toggleActive(d.id)}
                                data-testid={`toggle-${d.name.toLowerCase().replace(/\s/g, "-")}`}
                              />
                            </td>
                            <td className="p-3" onClick={e => e.stopPropagation()}>
                              <code className="text-[10px] font-mono bg-secondary px-1.5 py-0.5 rounded select-all">
                                {d.token?.slice(0, 8)}...
                              </code>
                            </td>
                          </tr>
                        ))}
                        {filtered.length === 0 && (
                          <tr>
                            <td colSpan={7} className="p-12 text-center">
                              <Truck className="w-10 h-10 mx-auto text-muted-foreground mb-3 opacity-50" />
                              <p className="text-muted-foreground">Nenhum entregador encontrado</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </>
            )}

            {tab === "ranking" && (
              <div className="space-y-3" data-testid="ranking-list">
                {[...deliverers]
                  .sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0) || (b.total_deliveries || 0) - (a.total_deliveries || 0))
                  .map((d, idx) => (
                    <Card
                      key={d.id}
                      className={`cursor-pointer hover:bg-secondary/20 transition-colors ${
                        idx === 0 && (d.total_ratings || 0) > 0 ? "border-yellow-400/50 bg-yellow-400/5" : ""
                      }`}
                      onClick={() => setSelectedId(d.id)}
                      data-testid={`ranking-row-${idx + 1}`}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          idx === 0 ? "bg-yellow-400 text-white" :
                          idx === 1 ? "bg-gray-400 text-white" :
                          idx === 2 ? "bg-amber-700 text-white" :
                          "bg-secondary text-muted-foreground"
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold">{d.name}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            {(d.total_ratings || 0) > 0 ? (
                              <>
                                <RatingStars rating={d.avg_rating || 0} />
                                <span className="text-sm font-bold">{(d.avg_rating || 0).toFixed(1)}</span>
                                <span className="text-xs text-muted-foreground">({d.total_ratings})</span>
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground">Sem avaliacoes</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">{d.total_deliveries || 0}</p>
                          <p className="text-[10px] text-muted-foreground uppercase">entregas</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                {deliverers.length === 0 && (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <BarChart3 className="w-10 h-10 mx-auto text-muted-foreground mb-3 opacity-50" />
                      <p className="text-muted-foreground">Sem entregadores com avaliacoes</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}
